/**
 * schemas/overview
 * ──────────────────────────────────────────────────────────────────────────
 * GET /v1/overview — the whole Finance Overview in ONE call: four KPI cards,
 * three charts, the needs-attention table and the gate map.
 *
 * That grouping is deliberate on the API side and matters here too: every
 * figure on the screen shares one as-of moment. Six separate requests would
 * produce a page whose KPI and whose chart disagree by a few seconds, and in a
 * cash subledger that is a support ticket.
 *
 * Invariant 4 is visible all over this file: `cash_in_custody` is a LIST of
 * per-currency totals, not a number, and the charts are base-currency-only and
 * say so in `labelled` rather than converting anything.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { z } from 'zod';
import { currency, currencyTotal, isoDate, minorNullable } from '../common';
import { gateStateSchema } from '../config/gates';

export const overviewKpisSchema = z.object({
  cash_in_custody: z.array(currencyTotal),
  /** Balances nobody has counted within the org's own staleDays. */
  unreconciled: z.array(currencyTotal),
  /** Magnitude of unresolved short/over — a short of 100 and an over of 100 is 200. */
  open_variance: z.array(currencyTotal),
  pending_approvals: z.number(),
  as_of: isoDate,
  /** Echoed so the card can say "older than 7 days" without a second call. */
  stale_days: z.number(),
});
export type OverviewKpis = z.infer<typeof overviewKpisSchema>;

export const chartsSchema = z.object({
  /** Null until a base currency is set — the charts then render their gate. */
  base_currency: currency.nullable(),
  /** The API's own label, e.g. "AED only". Rendered verbatim, never inferred. */
  labelled: z.string(),
  /** "Where the cash sits" — the stacked bar, split by scope. */
  by_scope: z.array(z.object({ scope: z.string(), total_minor: z.string(), count: z.number() })),
  /** "Who holds the most" — top five, each bar scaled to the largest. */
  top_holders: z.array(
    z.object({ holder: z.string(), holder_type: z.string(), total_minor: z.string() }),
  ),
  /** "Count results this period" — the segmented bar. */
  count_results: z.array(z.object({ status: z.string(), count: z.number() })),
});
export type Charts = z.infer<typeof chartsSchema>;

/** What each attention row is about — drives where a click goes. */
export const ATTENTION_KINDS = [
  'variance',
  'approval',
  'separation',
  'stale',
  'dead_letter',
] as const;
export type AttentionKind = (typeof ATTENTION_KINDS)[number];

export const attentionRowSchema = z.object({
  kind: z.string(),
  /** Higher is worse. The table is worst-first and the whole row is clickable. */
  severity: z.number(),
  label: z.string(),
  detail: z.string(),
  /** Null when the row is not about one assignment (a dead-letter batch). */
  assignment_id: z.string().nullable(),
  ref_id: z.string(),
  cur: currency.nullable(),
  amount_minor: minorNullable,
});
export type AttentionRow = z.infer<typeof attentionRowSchema>;

export const overviewSchema = z.object({
  gates: gateStateSchema,
  kpis: overviewKpisSchema,
  charts: chartsSchema,
  needs_attention: z.array(attentionRowSchema),
});
export type Overview = z.infer<typeof overviewSchema>;

/** True when the org has no assignments at all — the educational panel shows. */
export const isEmptyOverview = (o: Overview): boolean =>
  o.kpis.cash_in_custody.length === 0 && o.needs_attention.length === 0;
